import { CommonModule } from '@angular/common';
import { Component, computed, effect, input, signal } from '@angular/core';
import { ISkill } from 'src/app/interfaces/self-serve/iskill';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { QaiFieldComponent, QaiTextareaComponent, QaiTrimLeadingDirective, TextareaAutosizeDirective, QaiPasteCleanDirective, QaiFloatingSelectComponent, QaiFloatingSelectOption, QaiBadgeComponent } from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { DetailsSectionComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-container/details-section.component';
import { AccordionCardComponent } from 'src/app/components/shared/accordion-card/accordion-card.component';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { ScoringRefinementService } from 'src/app/services/self-serve/scoring-refinement.service';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { ISimulationPrompt } from 'src/app/interfaces/self-serve/isimulation-prompt';
import { IScoreWeight } from 'src/app/interfaces/self-serve/isimulation-score';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { CallRefinementPollingService } from 'src/app/services/self-serve/call-refinement-polling.service';
import { CallRefinementStatus } from 'src/app/utils/enums/self-serve/call-refinement-status.enum';

@Component({
  selector: 'skill-section',
  standalone: true,
  templateUrl: './skill-section.component.html',
  styleUrls: ['./skill-section.component.scss'],
  imports: [
    QaiFieldComponent,
    FormsModule,
    CommonModule,
    QaiTextareaComponent,
    TextareaAutosizeDirective,
    QaiTrimLeadingDirective,
    DetailsSectionComponent,
    ReactiveFormsModule,
    QaiPasteCleanDirective,
    QaiFloatingSelectComponent,
    QaiBadgeComponent,
    AccordionCardComponent,
    TranslocoModule
],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class SkillSectionComponent {
  form!: FormGroup;
  skillSection!: FormGroup;
  callUuid: string = '';

  isSelfAuthoring = input(false);

  readOnly = false;

  skillPromptLabel: string =
    'Enter the prompt that will be used to score the Skills section of the role play. *';

  rubricOptions: QaiFloatingSelectOption[] = [];
  private skills: ISkill[] = [];

  skillsWeight = signal(0);
  selectedSkill = signal<ISkill | null>(null);

  /** Reactive read of the shared refinement job status — re-syncs the
   * selected skill from the overview once a refinement started elsewhere
   * (General tab) completes, since this tab doesn't get remounted when
   * that happens. */
  private readonly pollingStatus = computed(() =>
    this.callRefinementPollingService.getStatus(this.callUuid),
  );

  onRubricChange(value: string): void {
    this.skillPromptGroup?.get('rubric')?.setValue(value);
    this.authoReactiveFormService.patchForm(
      this.callUuid,
      SimulationConstants.SKILL_PROMPT_PATH,
      { rubric: value }
    );
    this.authorService.updateSkillSelection(this.callUuid, value || null).subscribe({
      next: () => {
        if (this.isSelfAuthoring()) {
          this.scoringRefinementService.notifySaved(this.callUuid);
        }
      },
    });
    this.selectedSkill.set(this.skills.find((s) => s.uuid === value) ?? null);
  }

  constructor(
    private authorService: AuthorService,
    private route: ActivatedRoute,
    private authoReactiveFormService: AuthorReactiveFormService,
    private scoringRefinementService: ScoringRefinementService,
    private callRefinementPollingService: CallRefinementPollingService
  ) {
    effect(() => {
      if (this.pollingStatus() === CallRefinementStatus.Updated && this.isSelfAuthoring()) {
        console.log('Polling status updated, refreshing selected skill from overview...');
        this.refreshSelectedSkillFromOverview();
      }
    });
  }

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid')!;
    this.loadFormAndSetupAutoSave();
    if (this.isSelfAuthoring()) {
      this.loadSkillOptions();
    }
  }

  private loadSkillOptions(): void {
    forkJoin({
      skills: this.authorService.findActiveSkillsByAccount(this.callUuid),
      overview: this.authorService.getGeneratedCallDetails(this.callUuid),
    }).subscribe({
      next: ({ skills, overview }) => {
        this.skills = skills;
        this.rubricOptions = skills.map((skill) => ({
          value: skill.uuid,
          label: skill.name,
        }));
        if (overview.skillUuid) {
          this.skillPromptGroup?.get('rubric')?.setValue(overview.skillUuid, { emitEvent: false });
          this.selectedSkill.set(skills.find((s) => s.uuid === overview.skillUuid) ?? null);
        }
      },
    });
  }

  /** Re-fetches only the overview (not the skills list, which a refinement
   * doesn't change) and re-applies the selected skill/rubric so a
   * refinement that changed it is reflected without a full reload. */
  private refreshSelectedSkillFromOverview(): void {
    if (!this.callUuid) return;

    this.authorService.getGeneratedCallDetails(this.callUuid).subscribe({
      next: (overview) => {
        const rubricControl = this.skillPromptGroup?.get('rubric');
        rubricControl?.setValue(overview.skillUuid || '', { emitEvent: false });
        this.selectedSkill.set(
          overview.skillUuid ? this.skills.find((s) => s.uuid === overview.skillUuid) ?? null : null
        );
      },
    });
  }

  private setReadOnlyMode() {
    this.readOnly = this.authoReactiveFormService.isReadOnly(this.callUuid);
    this.authoReactiveFormService.setReadOnlyMode(
      this.callUuid,
      this.skillPromptGroup?.get('promptText')
    );
  }

  get skillPromptGroup(): FormGroup {
    return this.form?.get(
      SimulationConstants.SKILL_PROMPT_SECTION
    ) as FormGroup;
  }

  private loadFormAndSetupAutoSave(): void {
    this.authoReactiveFormService.getForm(this.callUuid)?.subscribe((form) => {
      this.form = form?.get('scoring') as FormGroup;
      this.skillSection = form?.get(
        SimulationConstants.SKILL_PROMPT_PATH
      ) as FormGroup;
      this.setupAutoSave(
        this.skillSection,
        SimulationConstants.SKILL_PROMPT_PATH
      );

      this.updateSkillsWeight(
        this.form?.get(SimulationConstants.SCORING_WEIGHTS_SECTION)?.value
      );
      this.setupSkillsWeightSubscription();

      this.setReadOnlyMode();
    });
  }

  private setupSkillsWeightSubscription(): void {
    const sectionWeightsControl = this.form?.get(
      SimulationConstants.SCORING_WEIGHTS_SECTION
    );
    if (!sectionWeightsControl) return;

    sectionWeightsControl.valueChanges
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((updatedWeights: IScoreWeight[]) => {
        this.updateSkillsWeight(updatedWeights);
      });
  }

  private updateSkillsWeight(weights: IScoreWeight[]): void {
    this.skillsWeight.set(
      weights?.find(
        (weight) => weight.section === SimulationConstants.SKILLS_SECTION
      )?.value ?? 0
    );
  }

  private setupAutoSave(section: FormGroup, formPath: string): void {
    const promptTextControl = section?.get('promptText');

    if (!promptTextControl) return;

    let previousValue: string = promptTextControl.getRawValue();

    promptTextControl.valueChanges
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((newVal) => {
        const oldValue = previousValue;
        previousValue = newVal;

        const updatedValue = section.getRawValue();

        const trimmedOld = (oldValue || '').trim();
        const trimmedNew = (newVal || '').trim();

        if (trimmedOld === trimmedNew && trimmedNew === '') {
          return;
        }

        this.authoReactiveFormService.patchForm(
          this.callUuid,
          formPath,
          updatedValue
        );

        const readOnly = this.authoReactiveFormService.isReadOnly(
          this.callUuid
        );

        if (updatedValue && !readOnly) {
          this.triggerPromptSave(this.callUuid, updatedValue, formPath);
        }
      });
  }

  private triggerPromptSave(
    callUuid: string,
    prompt: ISimulationPrompt,
    formPath: string
  ): void {
    if (this.isSelfAuthoring()) {
      this.scoringRefinementService.notifySaved(callUuid);
      return;
    }

    const payload = { prompts: [prompt] };

    const request$ = prompt.uuid
      ? this.authorService.updateSimulationPrompts(callUuid, payload.prompts)
      : this.authorService.createSimulationPrompts(callUuid, payload.prompts);

    request$?.subscribe((response) => {
      const updatedPrompt = response?.prompts?.[0];
      if (!updatedPrompt?.uuid) return;

      this.authoReactiveFormService.patchForm(callUuid, formPath, {
        uuid: updatedPrompt.promptText === '' ? null : updatedPrompt.uuid,
      });

      this.form.updateValueAndValidity();

      if (this.isSelfAuthoring()) {
        this.scoringRefinementService.notifySaved(callUuid);
      }
    });
  }
}
